// ============================================================================
// frontend/src/hooks/useTenantProvisioning.ts
// Hook propre : Gère uniquement la logique métier, pas le scroll
// ============================================================================

import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChainlitContext } from '@chainlit/react-client';
import { TenantFormData, ProvisioningSteps } from '@/types/tenant';
import confetti from 'canvas-confetti';

export const useTenantProvisioning = () => {
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [provisioningStatus, setProvisioningStatus] = useState<string | null>(null);
  const [provisioningSteps, setProvisioningSteps] = useState<ProvisioningSteps>({});
  const [showForm, setShowForm] = useState(true);
  const [deployedUrl, setDeployedUrl] = useState('');

  const fireConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const pollProvisioningStatus = async (tenantId: string) => {
    const maxAttempts = 240;
    let attempts = 0;
  
    const poll = async () => {
      try {
        setElapsedTime(attempts * 5);
        const statusResponse = await fetch(
          apiClient.buildEndpoint(`/api/provisioning/status/${tenantId}`)
        );
  
        if (!statusResponse.ok) {
          throw new Error('Impossible de récupérer le statut');
        }
  
        const statusData = await statusResponse.json();
        const steps = statusData.details?.steps || {};
      
        setProvisioningStatus(statusData.deployment_status);
        setProvisioningSteps(steps);
  
        const certificateStatus = steps.certificate;
        const isFullyDeployed = statusData.deployment_status === 'deployed' && 
                                certificateStatus === 'active';
  
        if (isFullyDeployed) {
          setSuccess(`🎉 Tenant déployé avec succès ! Le certificat SSL est actif.`);
          setDeployedUrl(statusData.url);
          setLoading(false);
          fireConfetti();
          return;
        }
  
        if (statusData.deployment_status === 'error') {
          setError(`Erreur: ${statusData.details?.error || 'Erreur inconnue'}`);
          setLoading(false);
          setShowForm(true);
          return;
        }
  
        const shouldContinuePolling = 
          statusData.deployment_status === 'provisioning' ||
          (statusData.deployment_status === 'deployed' && 
           ['provisioning', 'timeout'].includes(certificateStatus));
  
        attempts++;
        if (attempts < maxAttempts && shouldContinuePolling) {
          setTimeout(poll, 5000);
        } else if (attempts >= maxAttempts) {
          if (statusData.deployment_status === 'deployed') {
            setSuccess(`✅ Tenant déployé ! Le certificat SSL est encore en cours (peut prendre jusqu'à 30 minutes). Vous pouvez accéder au site via le dashboard.`);
            setDeployedUrl(statusData.url);
            setProvisioningStatus('deployed');
            setLoading(false);
          } else {
            setError(`Timeout après ${Math.floor(maxAttempts * 5 / 60)} minutes. Le provisioning continue en arrière-plan.`);
            setLoading(false);
            setShowForm(true);
          }
        }
      } catch (err: any) {
        setError(`Erreur polling: ${err.message}`);
        setLoading(false);
        setShowForm(true);
      }
    };
  
    await poll();
  };

  const handleSubmit = async (formData: TenantFormData) => {
    setLoading(true);
    setError(null);
    setSuccess('');
    setProvisioningStatus(null);
    setProvisioningSteps({});
    setShowForm(false);

    // ✅ Pas de validation ici - c'est fait dans le composant avant l'appel
    // Le hook ne gère que la logique métier (API, provisioning)

    try {
      const submitData = new FormData();
      
      // Basic info
      submitData.append('name', formData.name);
      submitData.append('subdomain', formData.subdomain);
      submitData.append('admin_email', formData.admin_email);
      submitData.append('admin_password', formData.admin_password);
      submitData.append('app_name', formData.app_name || formData.name);
      
      // Files
      if (formData.logo_light) submitData.append('logo_light', formData.logo_light);
      if (formData.logo_dark) submitData.append('logo_dark', formData.logo_dark);
      if (formData.favicon) submitData.append('favicon', formData.favicon);
      if (formData.login_page_image) submitData.append('login_page_image', formData.login_page_image);
      if (formData.custom_font_file) submitData.append('custom_font', formData.custom_font_file);
      
      // Theme colors
      submitData.append('primary_color', formData.primary_color);
      submitData.append('secondary_color', formData.secondary_color);
      submitData.append('accent_color', formData.accent_color);
      submitData.append('destructive_color', formData.destructive_color);
      submitData.append('dark_enabled', String(formData.dark_enabled));
      
      if (formData.dark_enabled) {
        submitData.append('dark_primary_color', formData.dark_primary_color);
        submitData.append('dark_background_color', formData.dark_background_color);
        submitData.append('dark_foreground_color', formData.dark_foreground_color);
        submitData.append('dark_card_color', formData.dark_card_color);
        submitData.append('dark_secondary_color', formData.dark_secondary_color);
        submitData.append('dark_accent_color', formData.dark_accent_color);
      }
      
      // Typography
      submitData.append('font_family', formData.font_family);
      submitData.append('font_mono', formData.font_mono);
      submitData.append('border_radius', formData.border_radius);
      
      // Sidebar
      submitData.append('sidebar_enabled', String(formData.sidebar_enabled));
      if (formData.sidebar_enabled) {
        submitData.append('sidebar_background_light', formData.sidebar_background_light);
        submitData.append('sidebar_background_dark', formData.sidebar_background_dark);
      }
      
      // OAuth
      submitData.append('oauth_providers', JSON.stringify(formData.oauth_providers));
      
      // ✅ AGENTS : Ajouter les agents sélectionnés au FormData
      console.log('📋 Selected agents to submit:', {
        count: formData.selected_agents.length,
        agents: formData.selected_agents
      });
      submitData.append('selected_agents', JSON.stringify(formData.selected_agents));

      // Debug: Afficher tout le FormData
      console.log('📤 FormData entries:');
      for (const [key, value] of submitData.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: [File] ${value.name}`);
        } else {
          console.log(`  ${key}:`, value);
        }
      }

      // ✅ ÉTAPE 1 : Créer le tenant dans la DB master
      setProvisioningStatus('creating_tenant');
      setProvisioningSteps({ tenant_db: 'creating' });

      const createResponse = await fetch(
        apiClient.buildEndpoint('/api/tenants/'),
        { method: 'POST', body: submitData }
      );

      if (!createResponse.ok) {
        const result = await createResponse.json();
        throw new Error(result.detail || 'Erreur lors de la création');
      }

      const tenantResult = await createResponse.json();
      const tenantId = tenantResult.id;
      const databaseName = `tenant_${formData.subdomain.replace(/-/g, '_')}`;

      setSuccess(`✓ Tenant créé (ID: ${tenantId})`);
      setProvisioningSteps({ tenant_db: 'created' });

      // ✅ ÉTAPE 2 : Lancer le provisioning avec les agents sélectionnés
      setProvisioningStatus('provisioning');
      setProvisioningSteps({ tenant_db: 'created', provisioning: 'starting' });
      
      // ✅ IMPORTANT : Envoyer les selected_agents directement dans le body du provisioning
      const provisionPayload = {
        tenant_id: tenantId,
        subdomain: formData.subdomain,
        database_name: databaseName,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        // ✅ NOUVEAU : Envoyer les agents sélectionnés
        selected_agents: formData.selected_agents.map(agent => ({
          agent_id: agent.agent_id,
          is_enabled: agent.is_enabled,
          is_default: agent.is_default,
          display_order: agent.display_order
        })),
        helm_values: {
          tenant_name: formData.name,
          app_name: formData.app_name || formData.name,
          oauth_google_enabled: formData.oauth_providers.some(p => p.type === 'google') ? 'true' : 'false',
          oauth_google_client_id: formData.oauth_providers.find(p => p.type === 'google')?.config.client_id || '',
          oauth_google_client_secret: formData.oauth_providers.find(p => p.type === 'google')?.config.client_secret || ''
        }
      };

      console.log('📤 Sending provision request:', {
        tenant_id: tenantId,
        selected_agents_count: formData.selected_agents.length,
        agents: formData.selected_agents
      });

      const provisionResponse = await fetch(
        apiClient.buildEndpoint('/api/provisioning/provision'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(provisionPayload)
        }
      );

      if (!provisionResponse.ok) {
        const result = await provisionResponse.json();
        throw new Error(result.detail || 'Erreur provisioning');
      }

      const provisionResult = await provisionResponse.json();
      console.log('✅ Provisioning started:', provisionResult);

      setProvisioningSteps({ tenant_db: 'created', provisioning: 'started' });
      await pollProvisioningStatus(tenantId);

    } catch (err: any) {
      console.error('❌ Provisioning error:', err);
      setError(err.message || 'Erreur');
      setProvisioningStatus('error');
      setLoading(false);
      setShowForm(true);
    }
  };

  return {
    loading,
    error,
    setError, // ✅ AJOUTÉ : Exposer setError pour la validation externe
    success,
    elapsedTime,
    provisioningStatus,
    provisioningSteps,
    showForm,
    deployedUrl,
    handleSubmit,
    navigate
  };
};